import dayjs from "dayjs";

export const getTime = (date: string) => new Date(date).getTime();

export const isUTC = (date: string) => date.toLowerCase().endsWith("z");

/**
 * Converts a Date object to UTC format while preserving the time values.
 * Example: 11:00 local time becomes 11:00Z, rather than adjusting for timezone offset
 */
export const convertToUtcPreserveTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ms = date.getMilliseconds();

  // Create a new Date in UTC with the same time components
  const utcDate = new Date(
    Date.UTC(year, month, day, hours, minutes, seconds, ms)
  );

  return utcDate.toISOString();
};

/**
 * Converts a UTC timestamp string back to a Date object while preserving the time values.
 * Example: "2023-06-15T11:00:00.000Z" becomes a Date object with 11:00 local time
 */
export const convertFromUtcPreserveTime = (utcString: string): Date => {
  const date = new Date(utcString);

  // Extract time components from the UTC date
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  const ms = date.getUTCMilliseconds();

  // Create a new local Date with the same time components
  return new Date(year, month, day, hours, minutes, seconds, ms);
};

/**
 * Converts date range to ISO timestamp strings.
 * Adds 1 day to the toDate to create an inclusive end range.
 * @param fromDate - Start date in any valid date format
 * @param toDate - End date in any valid date format (will have 1 day added)
 * @returns Object with fromTimestamp and toTimestamp as ISO strings, or null if invalid dates
 */
export const convertDateRangeToTimestamps = ({
  fromDate,
  toDate,
}: {
  fromDate: string;
  toDate: string;
}) => {
  const fromDayjs = dayjs.utc(fromDate);
  const toDayjs = dayjs.utc(toDate);

  if (!fromDayjs.isValid() || !toDayjs.isValid()) {
    console.error("Invalid date format");
    return null;
  }

  const fromTimestamp = fromDayjs.toISOString();
  const toTimestamp = toDayjs.add(1, "day").toISOString();

  return {
    fromTimestamp,
    toTimestamp,
  };
};

export const toSeconds = (ms: number) => Math.floor(ms / 1_000);
